import React, { useMemo, useState } from 'react';
import { useData } from '../context/DataContext';
import { X, Check } from 'lucide-react';
import { CATEGORIAS_PLANTILLA, CATEGORIAS_BASICAS, CategoriaPlantilla, claveCategoria } from '../config/categoriasPlantilla';
import { useLimite } from '../hooks/useLimite';
import { usePlus } from '../context/PlusContext';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

interface Props {
  onClose: () => void;
  /** Abierta sola al entrar por primera vez: cambia el tono del texto. */
  bienvenida?: boolean;
}

/**
 * Lista las categorías de la plantilla para que el usuario marque cuáles crear.
 * Las que ya tiene (mismo tipo y nombre) salen deshabilitadas, así que se puede
 * abrir tantas veces como se quiera sin duplicar nada.
 */
export const ModalPlantillaCategorias: React.FC<Props> = ({ onClose, bienvenida = false }) => {
  useCerrarConEscape(onClose);
  const { categorias, crearCategorias } = useData();
  const limite = useLimite('categorias');
  const { ofrecerPlus, ofrecerSiEsLimite } = usePlus();

  const existentes = useMemo(() => new Set(categorias.map(claveCategoria)), [categorias]);
  const disponibles = useMemo(
    () => CATEGORIAS_PLANTILLA.filter(c => !existentes.has(claveCategoria(c))),
    [existentes],
  );

  // Con Plus, todas marcadas de partida: lo normal es quitar alguna, no ir una a una.
  // En el plan gratis, solo las básicas que quepan en el tope.
  const [marcadas, setMarcadas] = useState<Set<string>>(() => new Set(
    (limite.maximo === Infinity
      ? disponibles
      : disponibles.filter(c => CATEGORIAS_BASICAS.has(c.nombre)).slice(0, limite.quedan)
    ).map(claveCategoria)));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const seleccion = disponibles.filter(c => marcadas.has(claveCategoria(c)));
  // Sin hueco para más: las que quedan sin marcar se desactivan
  const lleno = seleccion.length >= limite.quedan;

  const alternar = (c: CategoriaPlantilla) => {
    const k = claveCategoria(c);
    if (!marcadas.has(k) && lleno) { ofrecerPlus('categorias'); return; }
    setMarcadas(prev => {
      const s = new Set(prev);
      if (s.has(k)) s.delete(k); else s.add(k);
      return s;
    });
  };

  const marcarGrupo = (tipo: CategoriaPlantilla['tipo'], valor: boolean) => setMarcadas(prev => {
    const s = new Set(prev);
    let hueco = limite.quedan - s.size;
    disponibles.filter(c => c.tipo === tipo).forEach(c => {
      const k = claveCategoria(c);
      if (!valor) s.delete(k);
      else if (!s.has(k) && hueco > 0) { s.add(k); hueco--; }
    });
    return s;
  });

  const crear = async () => {
    if (seleccion.length === 0) return;
    setGuardando(true);
    setError('');
    try {
      await crearCategorias(seleccion);
      onClose();
    } catch (e: any) {
      if (ofrecerSiEsLimite(e)) { onClose(); return; }
      console.error(e);
      setError(e.message || 'No se pudieron crear las categorías');
    } finally {
      setGuardando(false);
    }
  };

  const grupo = (tipo: CategoriaPlantilla['tipo'], titulo: string, colorTitulo: string) => {
    const lista = CATEGORIAS_PLANTILLA.filter(c => c.tipo === tipo);
    const libres = disponibles.filter(c => c.tipo === tipo);
    const todasMarcadas = libres.length > 0 && libres.every(c => marcadas.has(claveCategoria(c)));
    return (
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className={`text-sm font-bold ${colorTitulo}`}>{titulo}</h3>
          {libres.length > 0 && (
            <button type="button" onClick={() => marcarGrupo(tipo, !todasMarcadas)}
              className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">
              {todasMarcadas ? 'Desmarcar todas' : 'Marcar todas'}
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {lista.map(c => {
            const k = claveCategoria(c);
            const yaExiste = existentes.has(k);
            const marcada = marcadas.has(k);
            const sinHueco = !yaExiste && !marcada && lleno;
            return (
              <button key={k} type="button" disabled={yaExiste} onClick={() => alternar(c)}
                aria-pressed={marcada}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left text-sm transition ${
                  yaExiste
                    ? 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                    : sinHueco
                      ? 'border-gray-200 dark:border-gray-700 opacity-50'
                    : marcada
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
                style={{ borderLeft: `4px solid ${c.color}` }}>
                <span className="text-lg shrink-0">{c.icono}</span>
                <span className="flex-1 min-w-0 truncate text-gray-900 dark:text-white">{c.nombre}</span>
                {yaExiste ? (
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 shrink-0">Ya la tienes</span>
                ) : (
                  <span className={`w-5 h-5 shrink-0 rounded border flex items-center justify-center ${
                    marcada ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {marcada && <Check className="w-3.5 h-3.5 text-white" />}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center capa-hoja"
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="hoja bg-white dark:bg-gray-800 w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl
                   max-h-full flex flex-col shadow-2xl">

        <div className="border-b border-gray-200 dark:border-gray-700 px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {bienvenida ? 'Empieza con tus categorías' : 'Categorías de plantilla'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {bienvenida
                ? 'Para clasificar gastos e ingresos necesitas categorías. Marca las que te sirvan; '
                  + 'si prefieres crearlas tú, cierra esto y hazlo en Configuración → Categorías.'
                : 'Marca las que quieras crear. Luego puedes editarlas o borrarlas como cualquier otra.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar"
            className="boton-cerrar text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                            text-red-700 dark:text-red-300 rounded-lg px-3 py-2 text-sm">
              {error}
            </div>
          )}
          {limite.maximo !== Infinity && (
            <p className="text-sm rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200">
              {limite.usados > 0
                ? `Tienes ${limite.usados} de ${limite.maximo} categorías. Has marcado ${seleccion.length} de las ${limite.quedan} que te quedan.`
                : `Has elegido ${seleccion.length} de ${limite.maximo}.`}
              {lleno && (
                <> {' '}<button type="button" onClick={() => ofrecerPlus('categorias')} className="font-semibold underline">
                  Con Plus, sin límite
                </button></>
              )}
            </p>
          )}
          {disponibles.length === 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Ya tienes todas las categorías de la plantilla.
            </p>
          )}
          {grupo('gasto', 'Gastos', 'text-red-600 dark:text-red-400')}
          {grupo('ingreso', 'Ingresos', 'text-green-600 dark:text-green-400')}
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-4 flex gap-2">
          <button onClick={crear} disabled={guardando || seleccion.length === 0}
            className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white
                       text-sm font-semibold transition disabled:opacity-50">
            {guardando
              ? 'Creando…'
              : seleccion.length === 0
                ? 'Marca alguna categoría'
                : `Crear ${seleccion.length} categoría${seleccion.length === 1 ? '' : 's'}`}
          </button>
          <button onClick={onClose}
            className="px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600
                       text-gray-700 dark:text-gray-300 text-sm font-semibold
                       hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            {bienvenida ? 'Ahora no' : 'Cancelar'}
          </button>
        </div>
      </div>
    </div>
  );
};
